import { useState } from 'react'
import { Alert, Button, Descriptions, Form, Input, Modal, Space, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { formatBytes, formatDateTime } from '@/shared/lib/format'
import { grantGuestAccess, type GuestAccessResult, type VenueZone } from './venueApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface FormValues {
  mac: string
  fullName?: string
  documentRef?: string
}

/** Backend `MacAddress` bilan bir xil: 6 bayt, `:` yoki `-` bilan */
const MAC_SHAPE = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/

interface Props {
  zone: VenueZone | null
  onClose: () => void
}

/**
 * Qabulxona yo'li — xodim mehmonga qo'lda kirish beradi.
 *
 * <h2>⚠️ FORMA ATAYLAB QISQA</h2>
 * Tezlik, kvota va muddat bu yerda YO'Q va qo'shilmaydi: ularni server
 * zona sozlamasidan oladi. Xodim faqat "qaysi qurilma, qaysi mehmon"
 * deydi. Bu `venue.guest.grant` huquqining butun ma'nosi — aks holda
 * ob'ekt menejeriga ixtiyoriy hisob yaratish huquqi berilardi.
 *
 * <h2>⚠️ ISM YOKI HUJJAT — KAMIDA BITTASI</h2>
 * Server ikkalasi ham bo'sh so'rovni rad etadi va sababi huquqiy:
 * qo'lda kiritish xodim hujjatni KO'RGANIGA tayanadi. Belgisiz yozuv
 * mehmonlar jurnalini foydasiz qilardi — hamma "qo'lda kiritilgan"
 * mehmon bitta qatorga yopishib qolardi.
 *
 * <h2>⚠️ PAROL BIR MARTA KO'RSATILADI</h2>
 * U hech qayerda saqlanmaydi: log'da ham, auditda ham, bazada ham yo'q.
 * Oyna yopilgach uni qayta olishning imkoni yo'q — buni foydalanuvchiga
 * AYTISH shart, aks holda u oynani yopib, mehmonni ulay olmay qoladi.
 */
export function StaffGuestAccessModal({ zone, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<GuestAccessResult | null>(null)

  const grant = useMutation({
    mutationFn: (values: FormValues) =>
      grantGuestAccess({
        zoneId: zone?.id ?? '',
        mac: values.mac.trim(),
        fullName: values.fullName?.trim() || null,
        documentRef: values.documentRef?.trim() || null,
      }),
    onSuccess: setResult,
    onError: (err) => setError(translateError(err, t)),
  })

  const close = () => {
    form.resetFields()
    setResult(null)
    setError(null)
    grant.reset()
    onClose()
  }

  /*
    ⚠️ IKKI TO'SIQ SERVERDA TEKSHIRILADI VA IKKALASI HAM 422 BERADI.
    Ularni oldindan aytmasak, xodim butun formani to'ldirib, tushunarsiz
    xato olardi — va sabab zona SOZLAMASIDA ekanini bilmasdi.
  */
  const methodAllowed = zone?.allowedMethods.includes('STAFF_MANUAL') ?? false
  // `STAFF_MANUAL` ning kuchi — DELEGATED. `SUFFICIENT` talab qiladigan
  // zona (masalan SMS majburiy bo'lgan) bu yo'lni rad etadi.
  const strengthOk = zone?.minStrength !== 'SUFFICIENT'
  const blocked = !methodAllowed || !strengthOk

  return (
    <Modal
      open={zone !== null}
      title={t('guestAccess.title', { zone: zone?.name ?? '' })}
      onCancel={close}
      destroyOnHidden
      footer={
        result ? (
          <Button type="primary" onClick={close}>
            {t('guestAccess.done')}
          </Button>
        ) : (
          <Space>
            <Button onClick={close}>{t('common.cancel')}</Button>
            <Button
              type="primary"
              loading={grant.isPending}
              disabled={blocked}
              onClick={() => submitOnValid(form, (values) => grant.mutate(values))}
            >
              {t('guestAccess.submit')}
            </Button>
          </Space>
        )
      }
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message={error}
          onClose={() => setError(null)}
        />
      ) : null}

      {result ? (
        result.hasAccess ? (
          <>
            {/*
              ⚠️ "Bir marta" ogohlantirishi NATIJANING TEPASIDA. Pastda
              tursa foydalanuvchi uni parolni ko'chirib olgandan keyin
              o'qirdi — ya'ni juda kech.
            */}
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('guestAccess.onceTitle')}
              description={t('guestAccess.onceHint')}
            />
            <Descriptions column={1} bordered size="small">
              {/*
                ⚠️ IKKI XIL LOGIN BOR VA ULAR ALMASHTIRILMAYDI:
                `login` — mehmon Wi-Fi formasiga TERADIGAN nom (realm bilan),
                `subscriberLogin` — panelda qidiriladigan toza login.
                Ilgari toza login berilgan va mehmon "ulandingiz" ekranini
                ko'rib, internetga chiqa olmagan.
              */}
              <Descriptions.Item label={t('guestAccess.login')}>
                <Typography.Text copyable strong>
                  {result.login}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('guestAccess.password')}>
                <Typography.Text copyable strong>
                  {result.password}
                </Typography.Text>
              </Descriptions.Item>
              {result.expiresAt ? (
                <Descriptions.Item label={t('guestAccess.expiresAt')}>
                  {formatDateTime(result.expiresAt)}
                </Descriptions.Item>
              ) : null}
              {result.quotaBytes != null ? (
                <Descriptions.Item label={t('guestAccess.quota')}>
                  {formatBytes(result.quotaBytes)}
                </Descriptions.Item>
              ) : null}
              {result.subscriberLogin ? (
                <Descriptions.Item label={t('guestAccess.internalLogin')}>
                  <Typography.Text type="secondary" copyable>
                    {result.subscriberLogin}
                  </Typography.Text>
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          </>
        ) : (
          /*
            ⚠️ BU XATO EMAS. Reklama bilan moliyalashtiriladigan zonada
            birinchi qadam faqat jurnalga yozadi; kirish mehmon rolikni
            ko'rgandan keyin ochiladi. Bo'sh login/parolni ko'rsatish
            yoki qizil xato chiqarish xodimni chalg'itardi.
          */
          <Alert
            type="info"
            showIcon
            message={t('guestAccess.journalOnlyTitle')}
            description={t('guestAccess.journalOnlyHint')}
          />
        )
      ) : (
        <>
          {!methodAllowed ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('guestAccess.methodBlockedTitle')}
              description={t('guestAccess.methodBlockedHint')}
            />
          ) : null}
          {methodAllowed && !strengthOk ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('guestAccess.strengthBlockedTitle')}
              description={t('guestAccess.strengthBlockedHint')}
            />
          ) : null}

          <Form form={form} layout="vertical" preserve={false} disabled={blocked}>
            <Form.Item
              name="mac"
              label={t('guestAccess.mac')}
              extra={t('guestAccess.macHint')}
              rules={[
                { required: true, message: t('guestAccess.macRequired') },
                { pattern: MAC_SHAPE, message: t('guestAccess.macShape') },
              ]}
            >
              <Input placeholder="AA:BB:CC:DD:EE:FF" autoFocus />
            </Form.Item>

            {/*
              ⚠️ Ikkalasi ham "majburiy" deb belgilanmaydi, lekin kamida
              bittasi shart — shuning uchun tekshiruv juft bo'yicha.
            */}
            <Form.Item
              name="fullName"
              label={t('guestAccess.fullName')}
              dependencies={['documentRef']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value: string) => {
                    // `getFieldValue` `any` qaytaradi — tipni shu yerda
                    // qotiramiz, aks holda bo'shliq tekshiruvi jimgina
                    // har qanday qiymatga "rost" bo'lardi.
                    const doc = getFieldValue('documentRef') as string | undefined
                    return value?.trim() || doc?.trim()
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('guestAccess.identityRequired')))
                  },
                }),
              ]}
            >
              <Input maxLength={200} />
            </Form.Item>

            <Form.Item
              name="documentRef"
              label={t('guestAccess.documentRef')}
              extra={t('guestAccess.documentRefHint')}
            >
              <Input maxLength={100} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  )
}
